/**
 * Способы входа в аккаунт: пароль и привязанные провайдеры (FR-A13).
 *
 * Отвязать последний способ нельзя — сервер такой запрос отклоняет, а кнопка
 * до него даже не доходит: пользователь должен видеть причину до нажатия,
 * а не после (docs/adr/0008-federated-login.md).
 */

import { useSearchParams } from 'react-router'

import { useLoginMethods, useOAuthLink, useOAuthProviders, useUnlinkProvider } from '@/api/oauth'
import { strings } from '@/lib/strings'

import { authErrorMessage } from './errorMessage'
import { providerName } from './providerName'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

export function LoginMethodsSection() {
  const methods = useLoginMethods()
  const providers = useOAuthProviders()
  const link = useOAuthLink('/me')
  const unlink = useUnlinkProvider()
  const [searchParams] = useSearchParams()
  // Колбэк привязки возвращает сюда с отметкой, какой провайдер добавлен.
  const justLinked = searchParams.get('linked')

  if (methods.isPending) {
    return <p className="mt-4 text-sm text-muted">{strings.profile.loginMethodsLoading}</p>
  }
  if (methods.data === undefined) {
    return null
  }

  const linked = new Map(methods.data.identities.map((item) => [item.provider, item]))
  const available = providers.data ?? []
  // Отвязка допустима, только если у пользователя останется чем войти.
  const canUnlink = methods.data.has_password || linked.size > 1

  return (
    <div className="mt-4 space-y-3">
      {justLinked !== null && (
        <p role="status" className="rounded-lg border border-success/50 p-3 text-sm text-success">
          {strings.profile.providerLinked(justLinked)}
        </p>
      )}

      <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3 text-sm">
        <span>
          {strings.profile.passwordMethod}
          <span className="ml-2 text-muted">
            {methods.data.has_password
              ? strings.profile.passwordMethodOn
              : strings.profile.passwordMethodOff}
          </span>
        </span>
      </div>

      {available.map((provider) => {
        const identity = linked.get(provider)
        return (
          <div
            key={provider}
            className="flex items-center justify-between gap-4 rounded-lg border border-border p-3 text-sm"
          >
            <span>
              {providerName(provider)}
              {identity && (
                <span className="ml-2 text-muted">
                  {identity.email ?? strings.profile.linkedAt(formatDate(identity.created_at))}
                </span>
              )}
            </span>

            {identity ? (
              <button
                type="button"
                disabled={!canUnlink || unlink.isPending}
                title={canUnlink ? undefined : strings.profile.lastMethodHint}
                onClick={() => {
                  unlink.mutate(provider)
                }}
                className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-60"
              >
                {strings.profile.unlinkProvider}
              </button>
            ) : (
              <button
                type="button"
                disabled={link.isPending}
                onClick={() => {
                  link.mutate(provider)
                }}
                className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-60"
              >
                {strings.profile.linkProvider}
              </button>
            )}
          </div>
        )
      })}

      {!canUnlink && linked.size > 0 && (
        <p className="text-xs text-muted">{strings.profile.lastMethodHint}</p>
      )}

      {(unlink.error !== null || link.error !== null) && (
        <p role="alert" className="rounded-lg border border-warning/50 p-3 text-sm text-warning">
          {authErrorMessage(unlink.error ?? link.error)}
        </p>
      )}
    </div>
  )
}
